import {
    ShaderMount,
    liquidMetalFragmentShader,
    LiquidMetalShapes,
    getShaderColorFromString,
} from "@paper-design/shaders";
import type { ShaderMountUniforms } from "@paper-design/shaders";

/**
 * Blazor JS-interop bridge for the Liquid Metal shader (@paper-design/shaders).
 *
 * Exposed on window.App as:
 *  - App.liquidMetalInit(elementId, options)     mount a shader into the container div
 *  - App.liquidMetalUpdate(elementId, options)   update uniforms / speed
 *  - App.liquidMetalDispose(elementId)           dispose the shader (WebGL context cleanup)
 *
 * Called from LiquidMetalSymbol.razor.cs.
 */

export interface LiquidMetalInteropOptions {
    colorBack?: string;
    colorTint?: string;
    shape?: string; // none | circle | daisy | diamond | metaballs
    repetition?: number;
    softness?: number;
    distortion?: number;
    contour?: number;
    shiftRed?: number;
    shiftBlue?: number;
    angle?: number;
    speed?: number;
    scale?: number;
    rotation?: number;
    fit?: string; // none | contain | cover
}

const mounts = new Map<string, ShaderMount>();

const SHAPE_VALUES = LiquidMetalShapes as unknown as Record<string, number>;
const FIT_VALUES: Record<string, number> = { none: 0, contain: 1, cover: 2 };

function buildUniforms(options: LiquidMetalInteropOptions): ShaderMountUniforms {
    const shapeKey = (options.shape ?? "none") as keyof typeof LiquidMetalShapes;
    const fitKey = (options.fit ?? "cover") as "none" | "contain" | "cover";

    return {
        // No image mask — we use the built-in abstract shapes only.
        u_isImage: false,
        u_image: undefined,
        u_shape: SHAPE_VALUES[shapeKey] ?? LiquidMetalShapes.none,
        u_colorBack: getShaderColorFromString(options.colorBack ?? "#AAAAAC"),
        u_colorTint: getShaderColorFromString(options.colorTint ?? "#FFFFFF"),
        u_repetition: options.repetition ?? 2,
        u_softness: options.softness ?? 0.1,
        u_distortion: options.distortion ?? 0.07,
        u_contour: options.contour ?? 0.4,
        u_shiftRed: options.shiftRed ?? 0.3,
        u_shiftBlue: options.shiftBlue ?? 0.3,
        u_angle: options.angle ?? 70,
        // Common sizing uniforms (see shader-sizing.d.ts).
        u_fit: FIT_VALUES[fitKey] ?? 2,
        u_scale: options.scale ?? 1,
        u_rotation: options.rotation ?? 0,
        u_originX: 0.5,
        u_originY: 0.5,
        u_offsetX: 0,
        u_offsetY: 0,
        u_worldWidth: 0,
        u_worldHeight: 0,
    };
}

export function liquidMetalInit(elementId: string, options: LiquidMetalInteropOptions): void {
    liquidMetalDispose(elementId);

    const container = document.getElementById(elementId);
    if (!container) return;

    try {
        const shader = new ShaderMount(
            container,
            liquidMetalFragmentShader,
            buildUniforms(options),
            undefined, // webGlContextAttributes
            options.speed ?? 1,
            0, // frame
        );
        mounts.set(elementId, shader);
    } catch (error) {
        console.error("[LiquidMetal] Failed to initialize shader:", error);
    }
}

export function liquidMetalUpdate(elementId: string, options: LiquidMetalInteropOptions): void {
    const shader = mounts.get(elementId);
    if (!shader) return;

    try {
        shader.setUniforms(buildUniforms(options));
        shader.setSpeed(options.speed ?? 1);
    } catch (error) {
        console.error("[LiquidMetal] Failed to update shader:", error);
    }
}

export function liquidMetalDispose(elementId: string): void {
    const shader = mounts.get(elementId);
    if (!shader) return;

    try {
        shader.dispose();
    } catch (error) {
        console.error("[LiquidMetal] Failed to dispose shader:", error);
    }
    mounts.delete(elementId);
}

// app.ts (which creates window.App) may run before or after this module inside the same esbuild
// bundle depending on the order of the entry file's imports. Attach immediately when possible and
// always re-attach on the next tick, once the whole bundle has finished executing.
const attachToApp = (): void => {
    const w = window as any;
    const app = w.App ?? (w.App = {});
    app.liquidMetalInit = liquidMetalInit;
    app.liquidMetalUpdate = liquidMetalUpdate;
    app.liquidMetalDispose = liquidMetalDispose;
};

attachToApp();
setTimeout(attachToApp, 0);

export {};
